import axios from 'axios';
import { getToken, removeToken } from '../auth';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

// Создаем экземпляр axios с базовой конфигурацией
const apiService = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Интерцептор для добавления токена авторизации
apiService.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Интерцептор для обработки ошибок
apiService.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      removeToken();
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

// ==================== API METHODS ====================

/**
 * Quality Control V2 API Methods
 */
export const qualityControlV2Api = {
  /**
   * Get operator scoreboard with metrics
   */
  getOperators: (startDate: string, endDate: string, projectId?: number) => {
    const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
    if (projectId) params.append('project_id', projectId.toString());
    return apiService.get(`/quality-control-v2/operators?${params.toString()}`);
  },

  /**
   * Get detailed stats for a specific operator
   */
  getOperatorDetails: (name: string, startDate: string, endDate: string) => {
    const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
    return apiService.get(`/quality-control-v2/operators/${encodeURIComponent(name)}?${params.toString()}`);
  },

  /**
   * Get list of scored calls with filters and pagination
   */
  getScoredCalls: (
    startDate?: string,
    endDate?: string,
    projectId?: number,
    operatorName?: string,
    limit: number = 50,
    offset: number = 0,
  ) => {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
    });
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (projectId) params.append('project_id', projectId.toString());
    if (operatorName) params.append('operator_name', operatorName);
    return apiService.get(`/quality-control-v2/calls?${params.toString()}`);
  },

  /**
   * Get details of a specific call scoring
   */
  getCallDetails: (callId: number) => {
    return apiService.get(`/quality-control-v2/calls/${callId}`);
  },

  /**
   * Get dropout analysis with anomaly detection
   */
  getDropoutAnalysis: (startDate: string, endDate: string) => {
    const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
    return apiService.get(`/quality-control-v2/dropout-analysis?${params.toString()}`);
  },

  /**
   * Get error analysis - most common operator errors
   */
  getErrorAnalysis: (startDate: string, endDate: string, projectId?: number, operatorName?: string) => {
    const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
    if (projectId) params.append('project_id', projectId.toString());
    if (operatorName) params.append('operator_name', operatorName);
    return apiService.get(`/quality-control-v2/error-analysis?${params.toString()}`);
  },

  /**
   * Get technical analysis - connection issues, volume, etc.
   */
  getTechnicalAnalysis: (startDate: string, endDate: string, projectId?: number, operatorName?: string) => {
    const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
    if (projectId) params.append('project_id', projectId.toString());
    if (operatorName) params.append('operator_name', operatorName);
    return apiService.get(`/quality-control-v2/technical-analysis?${params.toString()}`);
  },

  /**
   * Get objections analysis - how operators handle different objections
   */
  getObjectionsAnalysis: (startDate: string, endDate: string, projectId?: number) => {
    const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
    if (projectId) params.append('project_id', projectId.toString());
    return apiService.get(`/quality-control-v2/objections-analysis?${params.toString()}`);
  },

  /**
   * Get team diagnosis - overall weakness analysis for the team
   */
  getTeamDiagnosis: (startDate: string, endDate: string, projectId?: number) => {
    const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
    if (projectId) params.append('project_id', projectId.toString());
    return apiService.get(`/quality-control-v2/team-diagnosis?${params.toString()}`);
  },

  /**
   * Get error codes reference
   */
  getErrorCodes: () => {
    return apiService.get('/quality-control-v2/error-codes');
  },

  /**
   * Manually trigger scoring for a specific call
   */
  scoreCall: (callId: number) => {
    return apiService.post(`/quality-control-v2/score-call/${callId}`);
  },
};

export default apiService;
